import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class VideoScannerService {
  private readonly apiUrl = environment.baseUrl;

  private frameExtractionProgressSubject = new BehaviorSubject<any>({
    currentFrame: 0,
    totalFrames: 0,
    progress: 0,
    isComplete: false,
  });
  private frameExtractionCompleteSubject = new Subject<any[]>();
  private currentExtractionAbort: { cancelled: boolean } | null = null;

  frameExtractionProgress$ = this.frameExtractionProgressSubject.asObservable();
  frameExtractionComplete$ = this.frameExtractionCompleteSubject.asObservable();

  constructor(private http: HttpClient) {}

  createVideoScannerSession(payload: {
    video_name?: string | null;
    frame_interval?: number | null;
    ip_address?: string;
    location?: string;
    device?: string;
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/video-scanner/sessions`, payload || {});
  }

  appendVideoScannerFrame(
    sessionId: string,
    payload: {
      image: string;
      detect_payload: any;
      frame_index: number;
      timestamp_sec: number;
      image_file_name?: string | null;
      ip_address?: string;
      location?: string;
      device?: string;
    },
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/video-scanner/sessions/${sessionId}/frames`,
      payload,
    );
  }

  completeVideoScannerSession(
    sessionId: string,
    payload?: {
      frames_scanned?: number;
      faces_found?: number;
      credits_used?: number;
      ip_address?: string;
      location?: string;
      device?: string;
    },
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/video-scanner/sessions/${sessionId}/complete`,
      payload || {},
    );
  }

  async startFrameExtraction(
    videoElement: HTMLVideoElement,
    interval: number,
    duration: number,
  ): Promise<void> {
    this.frameExtractionProgressSubject.next({
      currentFrame: 0,
      totalFrames: 0,
      progress: 0,
      isComplete: false,
    });

    // setup cancellation token
    const abort = { cancelled: false };
    this.currentExtractionAbort = abort;

    try {
      await this.ensureMetadata(videoElement);

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Could not get canvas context');
      }

      canvas.width = videoElement.videoWidth || 1;
      canvas.height = videoElement.videoHeight || 1;

      const epsilon = 0.001;
      const clampedDuration = Math.max(0, duration - epsilon);
      const totalFrames = Math.max(
        1,
        Math.floor(clampedDuration / interval) + 1,
      );
      const frames: any[] = [];

      for (let i = 0; i < totalFrames; i++) {
        if (abort.cancelled) {
          break;
        }

        const time = Math.min(i * interval, clampedDuration);

        try {
          await this.seekTo(videoElement, time, 3000, abort);
        } catch {
          if (abort.cancelled) break;
          const retryTime = Math.max(0, time - Math.min(0.2, interval / 2));
          try {
            await this.seekTo(videoElement, retryTime, 3000, abort);
          } catch {
            if (abort.cancelled) break;
            this.updateProgress({
              currentFrame: i + 1,
              totalFrames,
              progress: ((i + 1) / totalFrames) * 100,
            });
            continue;
          }
        }

        if (abort.cancelled) break;

        await new Promise((resolve) => setTimeout(resolve, 15));

        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        const frameDataUrl = canvas.toDataURL('image/jpeg', 0.8);

        const frame = {
          index: i,
          timestamp: time,
          dataUrl: frameDataUrl,
          blob: await this.dataUrlToBlob(frameDataUrl),
        };

        frames.push(frame);

        const progressData = {
          currentFrame: i + 1,
          totalFrames,
          progress: ((i + 1) / totalFrames) * 100,
        };

        this.updateProgress(progressData);
      }

      if (!abort.cancelled) {
        this.completeExtraction(frames);
      } else {
        // ensure we reset state on cancel
        this.stopFrameExtraction();
      }
    } catch (error) {
      this.handleError(error);
    } finally {
      // clear token if it belongs to this run
      if (this.currentExtractionAbort === abort) {
        this.currentExtractionAbort = null;
      }
    }
  }

  stopFrameExtraction(): void {
    if (this.currentExtractionAbort) {
      this.currentExtractionAbort.cancelled = true;
    }
    this.frameExtractionProgressSubject.next({
      currentFrame: 0,
      totalFrames: 0,
      progress: 0,
      isComplete: false,
    });
  }

  private updateProgress(data: any): void {
    this.frameExtractionProgressSubject.next({
      currentFrame: data.currentFrame,
      totalFrames: data.totalFrames,
      progress: data.progress,
      isComplete: false,
    });
  }

  private completeExtraction(frames: any[]): void {
    this.frameExtractionProgressSubject.next({
      currentFrame: frames.length,
      totalFrames: frames.length,
      progress: 100,
      isComplete: true,
    });

    this.frameExtractionCompleteSubject.next(frames);
  }

  private handleError(error: any): void {
    this.stopFrameExtraction();
  }

  private async ensureMetadata(video: HTMLVideoElement): Promise<void> {
    if (!isFinite(video.duration) || video.readyState < 1) {
      await new Promise<void>((resolve, reject) => {
        const onLoaded = () => {
          cleanup();
          resolve();
        };
        const onError = () => {
          cleanup();
          reject(new Error('Video metadata error'));
        };
        const cleanup = () => {
          video.removeEventListener('loadedmetadata', onLoaded);
          video.removeEventListener('error', onError);
        };
        video.addEventListener('loadedmetadata', onLoaded, { once: true });
        video.addEventListener('error', onError, { once: true });
      });
    }
  }

  private async seekTo(
    video: HTMLVideoElement,
    time: number,
    timeoutMs = 3000,
    abort?: { cancelled: boolean },
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (abort?.cancelled) {
        reject(new Error('Aborted'));
        return;
      }

      let done = false;
      let rvfcCancel: (() => void) | undefined;

      const finish = (ok = true, err?: any) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        cleanup();
        ok ? resolve() : reject(err);
      };

      const onSeeked = () => finish(true);
      const onTimeupdate = () => {
        if (Math.abs(video.currentTime - target) < 0.05) finish(true);
      };
      const onError = () => finish(false, new Error('Video error during seek'));
      const onStalled = () => {
        // allow timeout to handle recovery
      };
      const onRVFC = () => finish(true);

      const onAbortCheck = () => {
        if (abort?.cancelled) finish(false, new Error('Aborted'));
      };

      const cleanup = () => {
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('timeupdate', onTimeupdate);
        video.removeEventListener('error', onError);
        video.removeEventListener('stalled', onStalled);
        if (rvfcCancel) rvfcCancel();
        if (abortInterval) clearInterval(abortInterval);
      };

      const epsilon = 0.001;
      const target = Math.max(
        0,
        Math.min(time, Math.max(0, video.duration - epsilon)),
      );
      video.pause();
      video.currentTime = target;

      video.addEventListener('seeked', onSeeked, { once: true });
      video.addEventListener('timeupdate', onTimeupdate);
      video.addEventListener('error', onError);
      video.addEventListener('stalled', onStalled);

      const anyVideo = video as any;
      if (typeof anyVideo.requestVideoFrameCallback === 'function') {
        const id = anyVideo.requestVideoFrameCallback(onRVFC);
        rvfcCancel = () => {
          if (typeof anyVideo.cancelVideoFrameCallback === 'function') {
            anyVideo.cancelVideoFrameCallback(id);
          }
        };
      }

      const timer = setTimeout(() => {
        finish(false, new Error('Seek timeout'));
      }, timeoutMs);
      const abortInterval = abort ? setInterval(onAbortCheck, 50) : null;
    });
  }

  private dataUrlToBlob(dataUrl: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to convert to blob'));
            }
          },
          'image/jpeg',
          0.8,
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    });
  }
}
